"use client";

import * as React from "react";
import { composeEventHandlers } from "@radix-ui/primitive";
import { Primitive } from "@radix-ui/react-primitive";
import {
  FileRejection,
  FileWithPath,
  useDropzone,
  type DropzoneOptions,
  type DropzoneState,
} from "react-dropzone";

export type DropzoneContextProps = DropzoneState & DropzoneOptions;

const DropzoneContext = React.createContext<DropzoneContextProps | undefined>(
  undefined
);

export const useDropzoneContext = (): DropzoneContextProps => {
  const context = React.useContext(DropzoneContext);
  if (!context) {
    throw new Error(
      "useDropzoneContext must be used within a DropzoneProvider"
    );
  }
  return context;
};

export interface DropzoneProps extends DropzoneOptions {
  children:
    | React.ReactNode
    | ((state: DropzoneContextProps) => React.ReactNode);
}

export const Dropzone: React.FC<DropzoneProps> = ({ children, ...props }) => {
  const state = useDropzone(props);
  const context = { ...state, ...props };

  return (
    <DropzoneContext.Provider value={context}>
      {typeof children === "function" ? children(context) : children}
    </DropzoneContext.Provider>
  );
};

Dropzone.displayName = "Dropzone";

export const DropzoneInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentPropsWithoutRef<"input">
>((props, ref): JSX.Element => {
  const { getInputProps, disabled } = useDropzoneContext();
  return (
    <Primitive.input ref={ref} {...getInputProps({ disabled, ...props })} />
  );
});

DropzoneInput.displayName = "DropzoneInput";

export const DropzoneZone = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>((props, ref): JSX.Element => {
  const { getRootProps, onDrop, onError, ...dropzoneState } =
    useDropzoneContext();

  return (
    <Primitive.div ref={ref} {...getRootProps(props)} {...dropzoneState} />
  );
});

DropzoneZone.displayName = "DropzoneZone";

export const DropzoneTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<"button">
>(({ onClick, ...props }, ref): JSX.Element => {
  const { open } = useDropzoneContext();
  return (
    <Primitive.button
      ref={ref}
      onClick={composeEventHandlers(onClick, open)}
      {...props}
    />
  );
});

DropzoneTrigger.displayName = "DropzoneTrigger";

export const DropzoneDragAccepted: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { isDragAccept } = useDropzoneContext();
  return isDragAccept ? <>{children}</> : null;
};

export const DropzoneDragRejected: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { isDragReject } = useDropzoneContext();
  return isDragReject ? <>{children}</> : null;
};

export const DropzoneDragDefault: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { isDragActive } = useDropzoneContext();
  return isDragActive ? null : <>{children}</>;
};

export const DropzoneAccepted: React.FC<{
  children: (files: Readonly<FileWithPath[]>) => React.ReactNode;
}> = ({ children }) => {
  const { acceptedFiles } = useDropzoneContext();
  return <>{children(acceptedFiles)}</>;
};

export const DropzoneRejected: React.FC<{
  children: (rejections: Readonly<FileRejection[]>) => React.ReactNode;
}> = ({ children }) => {
  const { fileRejections } = useDropzoneContext();
  return <>{children(fileRejections)}</>;
};

export {
  Dropzone as Root,
  DropzoneInput as Input,
  DropzoneZone as Zone,
  DropzoneTrigger as Trigger,
  DropzoneDragAccepted as DragAccepted,
  DropzoneDragRejected as DragRejected,
  DropzoneDragDefault as DragDefault,
  DropzoneAccepted as Accepted,
  DropzoneRejected as Rejected,
};
